import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
    Crown,
    Lightning,
    ArrowRight,
    CheckCircle,
    Infinity as InfinityIcon,
    Timer,
} from "@phosphor-icons/react";

const BENEFITS = [
    { id: "vault", text: "All current Recipes (Vanished Empire + future drops)" },
    { id: "playbooks", text: "All current Playbooks (Marketing Guide + Week 2/3/4)" },
    { id: "future", text: "Every future blueprint released forever" },
    { id: "badge", text: "Lifetime LIFETIME FOUNDER badge on the leaderboard" },
    { id: "cancel", text: "Cancel-proof — never billed again, ever" },
];

function formatCountdown(ms) {
    if (ms <= 0) return "00:00:00";
    const totalSeconds = Math.floor(ms / 1000);
    const h = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
    const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
    const s = String(totalSeconds % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
}

export default function LifetimeUpgrade() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [now, setNow] = useState(Date.now());

    // Tick every second whenever an eligibility window is active
    useEffect(() => {
        if (!user?.lifetime_upsell_eligible_until || user?.lifetime_upsell_used) return;
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [user?.lifetime_upsell_eligible_until, user?.lifetime_upsell_used]);

    if (!user || user.lifetime_member) return null;

    const eligibleUntilMs = user.lifetime_upsell_eligible_until
        ? new Date(user.lifetime_upsell_eligible_until).getTime()
        : 0;
    const remainingMs = eligibleUntilMs - now;
    const promoActive = !user.lifetime_upsell_used && remainingMs > 0;

    const upgrade = async () => {
        setLoading(true);
        try {
            const payload = { origin_url: window.location.origin };
            if (promoActive) payload.promo = "POST_BUMP_50_OFF";
            const r = await api.post("/stripe/create-lifetime-checkout", payload);
            window.location.href = r.data.url;
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upgrade failed");
            setLoading(false);
        }
    };

    return (
        <section
            data-testid="lifetime-upgrade-section"
            className="relative overflow-hidden bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A] to-[#0A1428] border border-electric-blue/40 neon-border"
        >
            {/* Decorative grid background */}
            <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
            <div className="absolute -top-12 -right-12 w-48 h-48 bg-electric-blue/15 rounded-full blur-3xl pointer-events-none" />

            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-6 p-6 md:p-8">
                {/* Left: Pitch */}
                <div className="lg:col-span-7">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-electric-blue/40 bg-electric-blue/10 mb-5">
                        <Crown size={14} weight="fill" className="text-electric-blue" />
                        <span className="label-mono text-[10px] text-electric-blue">
                            {promoActive
                                ? "POST-BUMP UPGRADE · ONE-TIME OFFER"
                                : "FOUNDER UPGRADE · ONE-TIME OFFER"}
                        </span>
                    </div>

                    <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter leading-none mb-4">
                        {promoActive ? (
                            <>
                                Save $50.{" "}
                                <span className="text-electric-blue neon-text">Lock the Vault.</span>
                            </>
                        ) : (
                            <>
                                Lock the entire Vault.
                                <br />
                                <span className="text-electric-blue neon-text">Forever.</span>
                            </>
                        )}
                    </h2>

                    <p className="text-sm md:text-base text-foreground/80 leading-relaxed mb-6 max-w-lg">
                        {promoActive ? (
                            <>
                                You just unlocked the Visual Authority Guide — your one-time
                                <span className="text-electric-blue font-bold"> $50 off Lifetime </span>
                                window is open. Pay once, own every Recipe, Playbook, and future drop.
                                <span className="text-white"> Never billed again.</span>
                            </>
                        ) : (
                            <>
                                Upgrade to{" "}
                                <span className="text-electric-blue font-bold">Lifetime Vault Access</span>{" "}
                                with a single one-time payment. Every Recipe. Every Playbook. Every future drop.
                                <span className="text-white"> No subscription. No recurring charge. Yours forever.</span>
                            </>
                        )}
                    </p>

                    <ul className="space-y-2.5 mb-6">
                        {BENEFITS.map((b) => (
                            <li
                                key={b.id}
                                data-testid={`lifetime-benefit-${b.id}`}
                                className="flex items-start gap-2.5 text-sm text-foreground/90"
                            >
                                <CheckCircle size={16} weight="fill" className="text-electric-blue mt-0.5 flex-shrink-0" />
                                <span>{b.text}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Right: Price + CTA */}
                <div className="lg:col-span-5 flex flex-col justify-center">
                    <div className="bg-black/60 backdrop-blur-xl border border-electric-blue/30 p-6 relative overflow-hidden">
                        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-electric-blue to-transparent" />
                        <div className="absolute -top-3 left-6 px-3 py-1 bg-electric-blue text-white">
                            <span className="label-mono text-[10px]">
                                {promoActive ? "$50 OFF" : "LIFETIME FOUNDER"}
                            </span>
                        </div>

                        <div className="mt-2 flex items-baseline gap-2 mb-1">
                            <span
                                data-testid="lifetime-price"
                                className="font-heading text-6xl font-black text-electric-blue"
                            >
                                {promoActive ? "$447" : "$497"}
                            </span>
                            <span className="label-mono text-xs">USD</span>
                        </div>
                        {promoActive && (
                            <p className="text-xs text-muted-foreground mb-1">
                                <span className="line-through">$497</span>{" "}
                                <span className="text-electric-blue font-bold">SAVE $50</span>
                            </p>
                        )}
                        <p className="label-mono text-[10px] text-muted-foreground mb-2">
                            ONE-TIME · NEVER BILLED AGAIN
                        </p>

                        {promoActive ? (
                            <div
                                data-testid="lifetime-countdown"
                                className="flex items-center gap-2 px-2 py-1.5 border border-electric-blue/30 bg-electric-blue/5 mb-5"
                            >
                                <Timer size={12} weight="bold" className="text-electric-blue animate-pulse" />
                                <span className="label-mono text-[9px] text-muted-foreground">EXPIRES IN</span>
                                <span className="font-mono text-xs font-bold text-electric-blue tracking-wider">
                                    {formatCountdown(remainingMs)}
                                </span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground line-through mb-5">
                                vs. <span>$240/yr forever</span>
                            </div>
                        )}

                        <button
                            onClick={upgrade}
                            disabled={loading}
                            data-testid="lifetime-upgrade-btn"
                            className="w-full bg-electric-blue hover:bg-electric-blue-dark disabled:opacity-50 text-white font-bold py-4 text-sm tracking-wider uppercase transition-all hover:shadow-[0_0_32px_rgba(0,123,255,0.7)] flex items-center justify-center gap-2 animate-glow-pulse"
                        >
                            {loading ? (
                                <>
                                    <Lightning size={16} weight="fill" className="animate-pulse" />
                                    REDIRECTING...
                                </>
                            ) : (
                                <>
                                    <InfinityIcon size={16} weight="bold" />
                                    {promoActive ? "CLAIM $50 OFF" : "GO LIFETIME"}
                                    <ArrowRight size={16} weight="bold" />
                                </>
                            )}
                        </button>

                        <p className="text-center text-[10px] text-muted-foreground mt-3 italic">
                            Secure one-time checkout via Stripe.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}
